import { buttonStyle } from '@florencelabs/button';
import clsx from 'clsx';
import React, { FC, Ref } from 'react';
import { Label, Radio, RadioGroupProps } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { RovingFocusRadioGroup } from '@provider-directory/components/forms/roving-focus-radio-group';
import { formatTimeTo12Hour } from '@provider-directory/functions/date';
import { ProviderTimeSlot } from '@provider-directory/types/scheduling.contracts';
export interface TimeSlotPickerProps extends RadioGroupProps {
  timeSlots: ProviderTimeSlot[];
  ref?: Ref<HTMLDivElement>;
}

type TimeOfDayKey = 'morning' | 'afternoon' | 'evening';

type GroupedTimeSlots = Record<TimeOfDayKey, ProviderTimeSlot[]>;

const groupSlotsByTimeOfDay = (slots: ProviderTimeSlot[]): GroupedTimeSlots =>
  slots.reduce<GroupedTimeSlots>(
    (acc, slot) => {
      if (slot.start < '12:00') {
        acc.morning.push(slot);
      } else if (slot.start >= '12:00' && slot.start < '17:00') {
        acc.afternoon.push(slot);
      } else {
        acc.evening.push(slot);
      }
      return acc;
    },
    { morning: [], afternoon: [], evening: [] },
  );

export const TimeSlotPicker: FC<TimeSlotPickerProps> = ({
  timeSlots,
  ref,
  ...props
}) => {
  const { t } = useTranslation();
  const groupedTimeSlots = groupSlotsByTimeOfDay(timeSlots);

  return (
    <RovingFocusRadioGroup
      data-testid={'time-slot-picker'}
      ref={ref}
      {...props}
    >
      <Label className={'sr-only'}>
        {t('appointmentSlot.appointmentTime.label')}
      </Label>
      {Object.entries(groupedTimeSlots)
        .filter(([_, slots]) => !!slots.length)
        .map(([groupNameKey, slots]) => (
          <div
            data-testid={`${groupNameKey}-slots-group`}
            className={'pt-6'}
            key={groupNameKey}
          >
            <span className={'text-display-2xsmall font-medium'}>
              {t(`schedule.${groupNameKey}`)}
            </span>
            <div className={'flex flex-wrap justify-start gap-2 pt-4'}>
              {slots.map((slot) => {
                const isSelected = slot.start === props.value;
                return (
                  <Radio
                    className={clsx(
                      '!mix-blend-unset w-[100px] !py-[10px] tablet:w-[117px] desktop:w-[100px]',
                      'data-[focus-visible]:outline data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-blue-600',
                      isSelected
                        ? '!bg-primary-color !text-white'
                        : '!bg-primary-200',
                      buttonStyle({ variant: 'clientTonal', size: 'small' }),
                    )}
                    data-testid={`time-slot-${slot.start}${
                      isSelected ? '-selected' : ''
                    }`}
                    key={slot.start}
                    value={slot.start}
                  >
                    {formatTimeTo12Hour(slot.start, {
                      noSpacing: true,
                      lowerCase: true,
                    })}
                  </Radio>
                );
              })}
            </div>
          </div>
        ))}
    </RovingFocusRadioGroup>
  );
};
